/**
 * Calculate the difference in days between current date
 * and the specified `isoDate`.
 *
 * For example:
 * > daysDiff('2021-03-24T00:00:00+00:00')
 *
 * Calculate the difference between now and 24 March, 2021.
 *
 * The function will return a nagative number if the specified
 * `isoDate` is later than current date (for example, tomorrow
 * date will result in `-1`).
 */
export function daysDiff(isoDate: string): number {
  const nowDate = new Date()
  const otherDate = new Date(isoDate)
  const millisecDiff = nowDate.getTime() - otherDate.getTime()
  return Math.floor(millisecDiff / (1000 * 60 * 60 * 24))
}

/**
 * Convert a Date to a local ISO date string (YYYY-MM-DD).
 *
 * Example
 * - If local time is 2025-11-03T08:15:00-08:00, returns "2025-11-03".
 *
 * This helper lives here, and not in `@/helpers/time`, because it is used by
 * `@/services/backend`, which is reachable from the public page bundles. The
 * `@/helpers/time` module imports luxon, and the public bundles must not
 * include it (see `RESTRICTED_LIBS` in `build/build-frontend.sh`).
 *
 * @param date Date instance to format (interpreted in local time).
 * @returns Local date string formatted as YYYY-MM-DD, or an empty string
 *   for an invalid date.
 */
export function toLocalISODate(date: Date): string {
  if (isNaN(date.getTime())) {
    return ''
  }

  const year = String(date.getFullYear()).padStart(4, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}
